import React, { useState, useEffect } from 'react'
import * as api from '../../app/axios/api'
import { toast } from 'sonner'
import {
  X,
  Plus,
  User,
  Eye,
  Edit,
  Trash2,
  ChevronUp,
  MessageSquare,
  History,
  Calendar,
  Clock,
} from 'lucide-react'
import FollowupForm from '../../components/FollowUpform'
import UpdateRecordForm from '../../components/RecordUpdatedForm'
const initialFollowupFormData = {
  record_id: '', // Will be set from a prop or context in a real app
  user_response: '',
  next_followup_date: '',
  interested_course: '',
  response_rating: 0,
  remark: '',
  call_at: '',
}
const today = new Date().toISOString().split('T')[0]
const Record = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [showCreateRecordForm, setShowCreateRecordForm] = useState(false) // Renamed for clarity
  const [showFollowupForm, setShowFollowupForm] = useState(false) // New state for FollowupForm visibility
  const [expandedRecordId, setExpandedRecordId] = useState(null)
  const [followupData, setFollowupData] = useState([])
  const [expandedFollowId, setExpandedFollowId] = useState(null)
  const [selectedRecordId, setSelectedRecordId] = useState(null) // To track which record to follow up
  const [deletingFollowupId, setDeletingFollowupId] = useState(null)
  const [showUpdateRecordForm, setShowUpdateRecordForm] = useState(false)
  const [recordToUpdate, setRecordToUpdate] = useState(null) // To hold the record data being updated
  // filtering states
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [initByFilter, setInitByFilter] = useState('')
  // State for selected date for filtering followups
  const [selectedDate, setSelectedDate] = useState(new Date()) // Default to today
  // State for delete confirmation
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [recordToDeleteId, setRecordToDeleteId] = useState(null)
  const [selectedFollowupId, setSelectedFollowupId] = useState(null)
  const [isUpdatingFollowup, setIsUpdatingFollowup] = useState(false)

  // const handleFollowupButtonClick = (recordId) => {
  //   setSelectedRecordId(recordId)
  //   setShowFollowupForm(true)
  // }

  const handleFollowupButtonClick = (
    recordId,
    followupId = null,
    isUpdate = false
  ) => {
    setSelectedRecordId(recordId)
    if (isUpdate && followupId) {
      setSelectedFollowupId(followupId)
      setIsUpdatingFollowup(true)
    } else {
      setSelectedFollowupId(null)
      setIsUpdatingFollowup(false)
    }
    setShowFollowupForm(true)
  }
  // const closeFollowupForm = () => {
  //   setShowFollowupForm(false)
  //   setSelectedRecordId(null)
  // }
  // State for the main Record creation form
  const closeFollowupForm = () => {
    setShowFollowupForm(false)
    setSelectedRecordId(null)
    setSelectedFollowupId(null)
    setIsUpdatingFollowup(false)
  }

  const [recordFormData, setRecordFormData] = useState({
    name: '',
    address: '',
    father: '',
    parent_number: '',
    mobile: '',
    class: '',
    stream_subject: '',
    collage_school: '',
    purpose_of_visitor: '',
    course_enquiry: '',
    contact_method: '',
    enquiry_status: 'enquiry',
    about_us_method: '',
    init_by: 'company',
    note: '',
  })

  const enquiryStatusOptions = ['enquiry', 'closed', 'admission', 'follow-up']
  const initByOptions = ['company', 'client']

  // --- API Functions for Records ---
  const createRecord = async (payload) => {
    try {
      setLoading(true)
      await api.createRecord(payload) // Assuming createRecord is in your api.js
      toast.success('Record Created Successfully')
      setShowCreateRecordForm(false) // Close the record creation form
      setRecordFormData({
        // Reset the record creation form
        name: '',
        address: '',
        father: '',
        parent_number: '',
        mobile: '',
        class: '',
        stream_subject: '',
        collage_school: '',
        purpose_of_visitor: '',
        course_enquiry: '',
        contact_method: '',
        enquiry_status: 'enquiry',
        about_us_method: '',
        init_by: 'company',
        note: '',
      })
      fetchRecords() // Refresh the list of records
    } catch (error) {
      console.error('Error creating record:', error)
      if (error?.message) {
        toast.error(error.response?.data.message)
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchRecords = async () => {
    try {
      setLoading(true)
      const res = await api.fetchRecord() // Assuming fetchRecord is in your api.js
      setData(res.data || [])
    } catch (error) {
      console.error('Error fetching records:', error)
      toast.error('Failed to fetch records data')
      setData([])
    } finally {
      setLoading(false)
    }
  }

  const fetchFollow = async (date) => {
    try {
      setLoading(true)
      // Ensure the date is in 'YYYY-MM-DD' format for the API
      const formattedDate =
        date instanceof Date ? date.toISOString().split('T')[0] : date
      const res = await api.fetchFollowUp(formattedDate) // Assuming fetchFollowUp is in your api.js
      setFollowupData(res.data)
    } catch (error) {
      console.error('Error fetching followups:', error)
      toast.error('Failed to fetch followup data')
      setFollowupData([])
    } finally {
      setLoading(false)
    }
  }

  const fetchFollowAStudent = async (id) => {
    try {
      const res = await api.fetchFollowById(id) // Assuming fetchFollowById is in your api.js
      setFollowupData(res.data)
    } catch (error) {
      toast.error('Failed to fetch records data')
      setFollowupData([])
    }
  }

  useEffect(() => {
    fetchRecords()
    fetchFollow(selectedDate) // Fetch followups for the initially selected date
  }, [selectedDate]) // Re-run when selectedDate changes

  // --- Handlers for Main Record Form ---
  const handleRecordInputChange = (e) => {
    const { name, value } = e.target
    setRecordFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleRecordSubmit = () => {
    if (!recordFormData.name.trim()) {
      toast.error('Name is required')
      return
    }
    createRecord(recordFormData)
  }

  const resetRecordForm = () => {
    setRecordFormData({
      name: '',
      address: '',
      father: '',
      parent_number: '',
      mobile: '',
      class: '',
      stream_subject: '',
      collage_school: '',
      purpose_of_visitor: '',
      course_enquiry: '',
      contact_method: '',
      enquiry_status: 'enquiry',
      about_us_method: '',
      init_by: 'company',
      note: '',
    })
    setShowCreateRecordForm(false)
  }

  const toggleExpand = (id) => {
    setExpandedRecordId((prevId) => (prevId === id ? null : id))
  }

  const toggleExpandFollow = (id) => {
    setExpandedFollowId((prevId) => (prevId === id ? null : id))
    fetchFollowAStudent(id)
  }

  // Function to open the custom delete confirmation dialog
  const openDeleteConfirmation = (recordId) => {
    setRecordToDeleteId(recordId)
    setShowDeleteConfirmation(true)
  }

  // Function to close the custom delete confirmation dialog
  const closeDeleteConfirmation = () => {
    setRecordToDeleteId(null)
    setShowDeleteConfirmation(false)
  }

  // Function to perform the actual delete after confirmation
  const confirmDelete = async () => {
    if (!recordToDeleteId) return

    try {
      setLoading(true)
      await api.deleteRecord(recordToDeleteId) // Assuming deleteRecord is in your api.js
      toast.success('Record Deleted Successfully')
      fetchRecords() // Refresh data
      closeDeleteConfirmation() // Close the dialog
    } catch (error) {
      console.error('Error deleting record:', error)
      toast.error('Failed to delete record')
    } finally {
      setLoading(false)
    }
  }
  // --- Handlers for Followup Form ----------------------------------------------------
  const [formData, setFormData] = React.useState(initialFollowupFormData)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [submissionMessage, setSubmissionMessage] = React.useState(null) // For success/error messages

  // This effect ensures formData.record_id is always in sync with currentRecordId
  // React.useEffect(() => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     record_id: selectedRecordId,
  //   }))
  // }, [selectedRecordId])

  React.useEffect(() => {
    if (isUpdatingFollowup && selectedFollowupId) {
      // Find the followup data from followupData array
      const existingFollowup = followupData.find(
        (f) => f.id === selectedFollowupId
      )

      if (existingFollowup) {
        setFormData({
          record_id: existingFollowup.record_id,
          user_response: existingFollowup.user_response || '',
          next_followup_date: existingFollowup.next_followup_date
            ? new Date(existingFollowup.next_followup_date)
                .toISOString()
                .split('T')[0]
            : '',
          interested_course: existingFollowup.interested_course || '',
          response_rating: existingFollowup.response_rating || 0,
          remark: existingFollowup.remark || '',
          call_at: existingFollowup.call_at
            ? new Date(existingFollowup.call_at).toISOString().slice(0, 16)
            : '',
          followup_completed: true, // Yahan true set kar rahe hain
        })
      }
    } else {
      setFormData({
        ...initialFollowupFormData,
        record_id: selectedRecordId,
      })
    }
  }, [isUpdatingFollowup, selectedFollowupId, selectedRecordId, followupData])

  // const handleFormSubmit = async (dataToSubmit) => {
  //   setIsSubmitting(true)
  //   // Clear previous messages

  //   try {
  //     // Format data for API call (you can keep this in the parent)
  //     const apiData = {
  //       ...dataToSubmit,
  //       record_id: parseInt(dataToSubmit.record_id), // Ensure it's an integer
  //       response_rating: parseInt(dataToSubmit.response_rating), // Ensure it's an integer
  //       next_followup_date: new Date(
  //         dataToSubmit.next_followup_date
  //       ).toISOString(),
  //       call_at: new Date(dataToSubmit.call_at).toISOString(),
  //     }

  //     const res = await api.createFollowUp(apiData)
  //     toast.success('Followup Created Successfully')
  //     handleFormReset()
  //     closeFollowupForm()
  //     fetchFollow(selectedDate) // Refresh followups for the current selected date
  //   } catch (error) {
  //     console.error(error)
  //     if (error?.message) {
  //       toast.error(error.response?.data.message)
  //     }
  //   } finally {
  //     setIsSubmitting(false)
  //   }
  // }

  // handleFormSubmit function ko replace karo
  const handleFormSubmit = async (dataToSubmit) => {
    setIsSubmitting(true)

    try {
      const apiData = {
        ...dataToSubmit,
        record_id: parseInt(dataToSubmit.record_id),
        response_rating: parseInt(dataToSubmit.response_rating),
        next_followup_date: new Date(
          dataToSubmit.next_followup_date
        ).toISOString(),
        call_at: new Date(dataToSubmit.call_at).toISOString(),
        followup_completed: dataToSubmit.followup_completed || false,
      }

      if (isUpdatingFollowup && selectedFollowupId) {
        // Update existing followup
        await api.updateFollowUp(selectedFollowupId, apiData)
        toast.success('Followup Updated Successfully')
      } else {
        // Create new followup
        await api.createFollowUp(apiData)
        toast.success('Followup Created Successfully')
      }

      handleFormReset()
      closeFollowupForm()
      fetchFollow(selectedDate)
    } catch (error) {
      console.error(error)
      if (error?.message) {
        toast.error(error.response?.data.message)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFormReset = () => {
    setFormData(initialFollowupFormData)
    setSubmissionMessage(null)
  }
  const handleDeleteFollowup = async (id) => {
    try {
      setDeletingFollowupId(id) // Set the ID of the followup being deleted
      await api.deleteFollowUp(id)
      toast.success('Followup Deleted Successfully')
      fetchFollow(selectedDate) // Refresh followups for the current selected date
    } catch (error) {
      console.error('Error deleting followup:', error)
      toast.error('Failed to delete followup')
    } finally {
      setDeletingFollowupId(null) // Reset the loading state
    }
  }

  const updateRecord = async (id, payload) => {
    try {
      setLoading(true)
      await api.UpdateRecord(id, payload)
      toast.success('Record Updated Successfully')
      closeUpdateRecordForm() // Close the update form
      fetchRecords() // Refresh the list of records
    } catch (error) {
      console.error('Error updating record:', error)
      if (error?.message) {
        toast.error(error.response?.data.message)
      }
    } finally {
      setLoading(false)
    }
  }
  const handleUpdateButtonClick = (record) => {
    setRecordToUpdate(record)
    setShowUpdateRecordForm(true)
  }

  const closeUpdateRecordForm = () => {
    setRecordToUpdate(null)
    setShowUpdateRecordForm(false)
  }

  const formatDate = (date) => {
    if (!(date instanceof Date)) {
      date = new Date(date)
    }
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // handleDateSelect function ko ye replace karo:
  const handleDateSelect = (dateString) => {
    const newDate = new Date(dateString)
    setSelectedDate(newDate)

    fetchTodayRecord(dateString) // API call with selected date
  }

  // fetchTodayRecord function ko ye replace karo (error state add karna padega):
  const fetchTodayRecord = async (date) => {
    try {
      setLoading(true)

      const currentDate = date || formatDate(new Date())

      // Make sure the date is being passed correctly
      const res = await api.fetchDateByRecord(currentDate)
    } catch (error) {
      console.error('Error fetching date records:', error)
      toast.error('Failed to fetch date records')
    } finally {
      setLoading(false)
    }
  }

  // useEffect mein ye change karo (line 186 ke paas):
  useEffect(() => {
    fetchRecords()
    fetchFollow(selectedDate)
    fetchTodayRecord(formatDate(selectedDate)) // Default current date ke saath call
  }, [selectedDate])

  return (
    <div className='min-h-screen bg-gray-50 p-2 md:p-4'>
      {/* Header */}
      <div className='mb-6'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white rounded-lg shadow-md p-4 md:p-6'>
          <div>
            <h1 className='text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2'>
              <User className='text-blue-600' />
              Records Management
            </h1>
            <p className='text-gray-600 mt-1'>
              Manage student and visitor records
            </p>
          </div>
          <button
            onClick={() => setShowCreateRecordForm(true)}
            disabled={loading}
            className='mt-4 sm:mt-0 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors duration-200'
          >
            <Plus size={20} />
            Create Record
          </button>
        </div>
      </div>

      {/* Data Display */}

      <div className=''>
        {/* Date Selector for Followups */}
        <div className='mb-6 '>
          <div className='bg-white rounded-xl shadow-md p-4 sticky top-30  z-10 md:p-6'>
            {/* Filter Row */}
            <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4'>
              {/* Search Input */}
              <input
                type='text'
                placeholder='Search by name or number'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200'
              />

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className='w-full md:w-1/4 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200'
              >
                <option value=''>All Status</option>
                {enquiryStatusOptions.map((option) => (
                  <option value={option} key={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </option>
                ))}
              </select>

              {/* Initiated By Filter */}
              <select
                value={initByFilter}
                onChange={(e) => setInitByFilter(e.target.value)}
                className='w-full md:w-1/4 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200'
              >
                <option value=''>All Initiated By</option>
                {initByOptions.map((option) => (
                  <option value={option} key={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </option>
                ))}
              </select>

              {/* Followup Date Picker */}
              <div className='relative w-full md:w-1/4'>
                <label
                  htmlFor='followup-date-picker'
                  className='absolute -top-2 left-3 bg-white px-1 text-xs text-gray-500'
                >
                  Followup Date
                </label>
                <input
                  id='followup-date-picker'
                  type='date'
                  value={formatDate(selectedDate)}
                  onChange={(e) => handleDateSelect(e.target.value)}
                  className='w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200'
                />
              </div>
            </div>
          </div>
        </div>
        {/* main table */}
        <div className='grid grid-cols-1 xl:grid-cols-12 gap-6'>
          {/* LEFT SIDE - Records Table */}
          <div className='xl:col-span-8 order-2 xl:order-1'>
            <div className='bg-white shadow-xl rounded-lg overflow-hidden'>
              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gradient-to-r from-blue-600 to-indigo-700'>
                    <tr>
                      <th className='px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider'>
                        Name
                      </th>
                      <th className='px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider'>
                        class
                      </th>
                      <th className='px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider'>
                        Mobile
                      </th>
                      <th className='px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider'>
                        Enquiry Status
                      </th>
                      <th className='px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider'>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {/* ***************************************************************** */}
                    {data
                      .filter(
                        (record) =>
                          (record.name
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                            record.mobile?.includes(searchQuery) ||
                            record.parent_number?.includes(searchQuery)) &&
                          (statusFilter
                            ? record.enquiry_status === statusFilter
                            : true) &&
                          (initByFilter
                            ? record.init_by === initByFilter
                            : true)
                      )
                      .map((record) => (
                        <React.Fragment key={record.id}>
                          <tr className='hover:bg-blue-50 transition-colors duration-200 ease-in-out'>
                            <td className='px-4 py-4 whitespace-nowrap'>
                              <div className='flex items-center'>
                                <div className='flex-shrink-0 h-10 w-10'>
                                  <div className='h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shadow-sm'>
                                    <User className='h-5 w-5 text-blue-600' />
                                  </div>
                                </div>
                                <div className='ml-4'>
                                  <div className='text-sm font-medium text-gray-900'>
                                    {record.name}
                                  </div>
                                  <div className='text-xs text-gray-500'>
                                    ID: {record.id}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className='px-4 py-4 whitespace-nowrap'>
                              <div className='text-sm text-gray-900'>
                                {record.class || 'N/A'}
                              </div>
                            </td>

                            <td className='px-4 py-4 whitespace-nowrap'>
                              <div className='text-sm text-gray-900'>
                                {record.mobile || 'N/A'}
                              </div>
                            </td>
                            <td className='px-4 py-4 whitespace-nowrap'>
                              <span
                                className={`inline-flex px-3 py-1 text-xs font-medium leading-5 rounded-full shadow-sm ${
                                  record.enquiry_status === 'admission'
                                    ? 'bg-green-100 text-green-800'
                                    : record.enquiry_status === 'enquiry'
                                    ? 'bg-blue-100 text-blue-800'
                                    : record.enquiry_status === 'closed'
                                    ? 'bg-gray-100 text-gray-800'
                                    : record.enquiry_status === 'converted'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : record.enquiry_status === 'follow-up'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-purple-100 text-purple-800'
                                }`}
                              >
                                {record.enquiry_status &&
                                typeof record.enquiry_status === 'string'
                                  ? record.enquiry_status
                                      .charAt(0)
                                      .toUpperCase() +
                                    record.enquiry_status
                                      .slice(1)
                                      .replace('_', ' ')
                                  : 'N/A'}
                              </span>
                            </td>
                            <td className='px-4 py-4 whitespace-nowrap text-right text-sm font-medium'>
                              <div className='flex items-center justify-center space-x-2 md:space-x-3'>
                                {/* 🔴 Follow Up Button */}
                                <button
                                  onClick={() =>
                                    handleFollowupButtonClick(record.id)
                                  }
                                  className='text-blue-600 hover:text-blue-800 p-2 rounded-full bg-blue-50 hover:bg-blue-100 transition-all duration-200 ease-in-out shadow-sm'
                                  title='Follow Up'
                                >
                                  <MessageSquare size={18} />
                                </button>

                                {/* 🕓 History Button */}
                                <button
                                  onClick={() => toggleExpandFollow(record.id)}
                                  className='text-amber-600 hover:text-amber-800 p-2 rounded-full bg-amber-50 hover:bg-amber-100 transition-all duration-200 ease-in-out shadow-sm'
                                  title='View History'
                                >
                                  <History size={18} />
                                </button>

                                {/* 🗑️ Delete Button */}
                                <button
                                  onClick={() =>
                                    openDeleteConfirmation(record.id)
                                  }
                                  className='text-red-600 hover:text-red-800 p-2 rounded-full bg-red-50 hover:bg-red-100 transition-all duration-200 ease-in-out shadow-sm'
                                  title='Delete Record'
                                >
                                  <Trash2 size={18} />
                                </button>

                                {/* ✏️ Edit Button */}
                                <button
                                  onClick={() =>
                                    handleUpdateButtonClick(record)
                                  }
                                  className='text-green-600 hover:text-green-800 p-2 rounded-full bg-green-50 hover:bg-green-100 transition-all duration-200 ease-in-out shadow-sm'
                                  title='Edit Record'
                                >
                                  <Edit size={18} />
                                </button>

                                {/* 👁️ Expand / Collapse Button */}
                                <button
                                  onClick={() => toggleExpand(record.id)}
                                  className='text-gray-600 hover:text-gray-800 p-2 rounded-full bg-gray-50 hover:bg-gray-100 transition-all duration-200 ease-in-out shadow-sm'
                                  title={
                                    expandedRecordId === record.id
                                      ? 'Collapse Details'
                                      : 'Expand Details'
                                  }
                                >
                                  {expandedRecordId === record.id ? (
                                    <ChevronUp size={18} />
                                  ) : (
                                    <Eye size={18} />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedRecordId === record.id && (
                            <tr className='bg-blue-50 border-t border-blue-200'>
                              <td colSpan='5' className='p-4 md:p-6'>
                                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 text-sm text-gray-700'>
                                  <div className='flex flex-col'>
                                    <strong className='font-semibold text-gray-900'>
                                      Parent Number:
                                    </strong>{' '}
                                    <span>{record.parent_number || 'N/A'}</span>
                                  </div>
                                  <div className='flex flex-col'>
                                    <strong className='font-semibold text-gray-900'>
                                      Class:
                                    </strong>{' '}
                                    <span>{record.class || 'N/A'}</span>
                                  </div>
                                  <div className='flex flex-col'>
                                    <strong className='font-semibold text-gray-900'>
                                      Stream/Subject:
                                    </strong>{' '}
                                    <span>
                                      {record.stream_subject || 'N/A'}
                                    </span>
                                  </div>
                                  <div className='flex flex-col'>
                                    <strong className='font-semibold text-gray-900'>
                                      College/School:
                                    </strong>{' '}
                                    <span>
                                      {record.collage_school || 'N/A'}
                                    </span>
                                  </div>
                                  <div className='flex flex-col'>
                                    <strong className='font-semibold text-gray-900'>
                                      Purpose of Visit:
                                    </strong>{' '}
                                    <span>
                                      {record.purpose_of_visitor || 'N/A'}
                                    </span>
                                  </div>
                                  <div className='flex flex-col'>
                                    <strong className='font-semibold text-gray-900'>
                                      Course Enquiry:
                                    </strong>{' '}
                                    <span>
                                      {record.course_enquiry || 'N/A'}
                                    </span>
                                  </div>
                                  <div className='flex flex-col'>
                                    <strong className='font-semibold text-gray-900'>
                                      Contact Method:
                                    </strong>{' '}
                                    <span>
                                      {record.contact_method || 'N/A'}
                                    </span>
                                  </div>

                                  <div className='flex flex-col'>
                                    <strong className='font-semibold text-gray-900'>
                                      About Us Method:
                                    </strong>{' '}
                                    <span>
                                      {record.about_us_method || 'N/A'}
                                    </span>
                                  </div>
                                  <div className='flex flex-col'>
                                    <strong className='font-semibold text-gray-900'>
                                      Initiated By:
                                    </strong>{' '}
                                    <span>
                                      {record.init_by &&
                                      typeof record.init_by === 'string'
                                        ? record.init_by
                                            .charAt(0)
                                            .toUpperCase() +
                                          record.init_by.slice(1)
                                        : 'N/A'}
                                    </span>
                                  </div>
                                  <div className='md:col-span-2 lg:col-span-3 flex flex-col'>
                                    <strong className='font-semibold text-gray-900'>
                                      Notes:
                                    </strong>{' '}
                                    <span>{record.note || 'N/A'}</span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          {expandedFollowId === record.id && (
                            <tr className='bg-indigo-50 border-t-2 border-indigo-300'>
                              <td colSpan='5' className='p-4 md:p-6'>
                                <h3 className='text-lg font-bold text-indigo-700 mb-4 border-b pb-2'>
                                  Followup History for {record.name}
                                </h3>
                                {followupData.filter(
                                  (f) => f.record_id === record.id
                                ).length > 0 ? (
                                  <div className='space-y-4'>
                                    {followupData
                                      .filter((f) => f.record_id === record.id)
                                      .map((followup) => (
                                        <div
                                          key={followup.id}
                                          className='bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200'
                                        >
                                          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 text-sm text-gray-700'>
                                            <div className='flex flex-col'>
                                              <span className='font-semibold text-gray-900'>
                                                Followup ID:
                                              </span>
                                              <span>{followup.id}</span>
                                            </div>
                                            <div className='flex flex-col'>
                                              <span className='font-semibold text-gray-900'>
                                                User Response:
                                              </span>
                                              <span>
                                                {followup.user_response}
                                              </span>
                                            </div>
                                            <div className='flex flex-col'>
                                              <span className='font-semibold text-gray-900'>
                                                Next Followup:
                                              </span>
                                              <span>
                                                {followup.next_followup_date}
                                              </span>
                                            </div>
                                            <div className='flex flex-col'>
                                              <span className='font-semibold text-gray-900'>
                                                Interested Course:
                                              </span>
                                              <span>
                                                {followup.interested_course}
                                              </span>
                                            </div>
                                            <div className='flex flex-col'>
                                              <span className='font-semibold text-gray-900'>
                                                Rating:
                                              </span>
                                              <span>
                                                {followup.response_rating}
                                              </span>
                                            </div>
                                            <div className='flex flex-col col-span-1 sm:col-span-2'>
                                              <span className='font-semibold text-gray-900'>
                                                Remark:
                                              </span>
                                              <span>{followup.remark}</span>
                                            </div>
                                            <div className='flex flex-col'>
                                              <span className='font-semibold text-gray-900'>
                                                Call At:
                                              </span>
                                              <span>
                                                {new Date(
                                                  followup.call_at
                                                ).toLocaleString()}
                                              </span>
                                            </div>
                                            <div className='flex flex-col'>
                                              <span className='font-semibold text-gray-900'>
                                                Created At:
                                              </span>
                                              <span>
                                                {new Date(
                                                  followup.created_at
                                                ).toLocaleString()}
                                              </span>
                                            </div>
                                            <div className='flex items-center justify-end col-span-full mt-2'>
                                              <button
                                                onClick={() =>
                                                  handleDeleteFollowup(
                                                    followup.id
                                                  )
                                                }
                                                className='px-3 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition-colors duration-200 shadow-sm'
                                                disabled={
                                                  deletingFollowupId ===
                                                  followup.id
                                                }
                                              >
                                                {deletingFollowupId ===
                                                followup.id ? (
                                                  <div className='flex items-center'>
                                                    <svg
                                                      className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                                                      xmlns='http://www.w3.org/2000/svg'
                                                      fill='none'
                                                      viewBox='0 0 24 24'
                                                    >
                                                      <circle
                                                        className='opacity-25'
                                                        cx='12'
                                                        cy='12'
                                                        r='10'
                                                        stroke='currentColor'
                                                        strokeWidth='4'
                                                      ></circle>
                                                      <path
                                                        className='opacity-75'
                                                        fill='currentColor'
                                                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                                      ></path>
                                                    </svg>
                                                    Deleting...
                                                  </div>
                                                ) : (
                                                  'Delete Followup'
                                                )}
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                ) : (
                                  <p className='text-gray-500 text-center py-4'>
                                    No followup history found for this record.
                                  </p>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}

                    {/* ***************************************************************** */}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Today's Followup History */}
          <div className='xl:col-span-4 order-1 xl:order-2'>
            <div className='bg-white shadow-xl rounded-lg overflow-hidden xl:sticky xl:top-6'>
              <div className='p-4 md:p-6 border-b bg-gradient-to-r from-orange-500 to-amber-600'>
                <h2 className='text-xl font-bold text-white flex items-center gap-2'>
                  <Calendar className='text-white' />
                  Today's Followup Schedule
                </h2>
                <p className='text-white text-sm mt-1 flex items-center gap-1'>
                  <Clock size={14} />
                  {new Date().toLocaleDateString('en-IN', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>

              <div className='p-4 md:p-6 max-h-[calc(100vh-200px)] overflow-y-auto'>
                {followupData.filter((f) => f.next_followup_date === today)
                  .length === 0 ? (
                  <div className='text-center py-12'>
                    <Calendar className='mx-auto h-12 w-12 text-gray-400' />
                    <h3 className='mt-2 text-sm font-medium text-gray-900'>
                      No followups scheduled
                    </h3>
                    <p className='mt-1 text-sm text-gray-500'>
                      No followups scheduled for today.
                    </p>
                  </div>
                ) : (
                  <div className='space-y-4'>
                    {followupData
                      .filter((f) => f.next_followup_date === today)
                      .map((followup) => {
                        const record = data.find(
                          (r) => r.id === followup.record_id
                        )
                        const isCompleted = followup.followup_completed === true

                        return (
                          <div
                            key={followup.id}
                            className={`border-l-4 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200 ${
                              isCompleted
                                ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-500'
                                : 'bg-gradient-to-br from-amber-50 to-orange-50 border-orange-500'
                            }`}
                          >
                            <div className='flex items-start justify-between mb-3'>
                              <div className='flex-1'>
                                <div className='flex items-center gap-2'>
                                  <h3 className='text-lg font-bold text-gray-900 flex items-center gap-2'>
                                    <User
                                      className={`h-5 w-5 ${
                                        isCompleted
                                          ? 'text-green-600'
                                          : 'text-orange-600'
                                      }`}
                                    />
                                    {record?.name || 'Unknown'}
                                  </h3>
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                      isCompleted
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}
                                  >
                                    {isCompleted ? 'Completed' : 'Pending'}
                                  </span>
                                </div>
                                <p className='text-xs text-gray-500 mt-1'>
                                  Followup ID: {followup.id} | Record ID:{' '}
                                  {followup.record_id}
                                </p>
                              </div>
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                  isCompleted
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-orange-100 text-orange-800'
                                }`}
                              >
                                <Clock size={12} className='mr-1' />
                                {new Date(followup.call_at).toLocaleTimeString(
                                  'en-IN',
                                  {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  }
                                )}
                              </span>
                            </div>

                            <div className='space-y-2 text-sm'>
                              <div className='flex items-start'>
                                <span className='font-semibold text-gray-700 min-w-[120px]'>
                                  Response:
                                </span>
                                <span className='text-gray-900'>
                                  {followup.user_response}
                                </span>
                              </div>
                              <div className='flex items-start'>
                                <span className='font-semibold text-gray-700 min-w-[120px]'>
                                  Course:
                                </span>
                                <span className='text-gray-900'>
                                  {followup.interested_course}
                                </span>
                              </div>
                              <div className='flex items-start'>
                                <span className='font-semibold text-gray-700 min-w-[120px]'>
                                  Rating:
                                </span>
                                <div className='flex items-center gap-1'>
                                  {[...Array(5)].map((_, i) => (
                                    <span
                                      key={i}
                                      className={`text-lg ${
                                        i < followup.response_rating
                                          ? 'text-yellow-500'
                                          : 'text-gray-300'
                                      }`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                  <span className='ml-2 text-gray-600'>
                                    ({followup.response_rating}/5)
                                  </span>
                                </div>
                              </div>
                              {followup.remark && (
                                <div className='flex items-start'>
                                  <span className='font-semibold text-gray-700 min-w-[120px]'>
                                    Remark:
                                  </span>
                                  <span className='text-gray-900'>
                                    {followup.remark}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className='mt-4 pt-3 border-t border-gray-200 flex gap-2'>
                              {!isCompleted && (
                                <button
                                  onClick={() =>
                                    handleFollowupButtonClick(
                                      followup.record_id,
                                      followup.id,
                                      true
                                    )
                                  }
                                  className='flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2'
                                >
                                  <MessageSquare size={16} />
                                  Update Follow Up
                                </button>
                              )}
                              {isCompleted && (
                                <div className='flex-1 bg-green-100 text-green-800 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2'>
                                  <svg
                                    className='w-5 h-5'
                                    fill='currentColor'
                                    viewBox='0 0 20 20'
                                  >
                                    <path
                                      fillRule='evenodd'
                                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                                      clipRule='evenodd'
                                    />
                                  </svg>
                                  Completed Today
                                </div>
                              )}
                              <button
                                onClick={() =>
                                  handleDeleteFollowup(followup.id)
                                }
                                disabled={deletingFollowupId === followup.id}
                                className='bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50'
                              >
                                {deletingFollowupId === followup.id ? (
                                  <svg
                                    className='animate-spin h-5 w-5 text-white'
                                    xmlns='http://www.w3.org/2000/svg'
                                    fill='none'
                                    viewBox='0 0 24 24'
                                  >
                                    <circle
                                      className='opacity-25'
                                      cx='12'
                                      cy='12'
                                      r='10'
                                      stroke='currentColor'
                                      strokeWidth='4'
                                    ></circle>
                                    <path
                                      className='opacity-75'
                                      fill='currentColor'
                                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                    ></path>
                                  </svg>
                                ) : (
                                  <Trash2 size={16} />
                                )}
                              </button>
                            </div>
                          </div>
                        )
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Record Creation Form Modal */}
      {showCreateRecordForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden'>
            <div className='flex items-center justify-between p-4 md:p-6 border-b'>
              <h2 className='text-xl md:text-2xl font-bold text-gray-800'>
                Create New Record
              </h2>
              <button
                onClick={resetRecordForm}
                className='text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100'
              >
                <X size={24} />
              </button>
            </div>

            <div className='overflow-y-auto max-h-[calc(90vh-140px)]'>
              <div className='p-4 md:p-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6'>
                  {/* Basic Information */}
                  <div className='lg:col-span-3'>
                    <h3 className='text-lg font-semibold text-gray-800 mb-4 border-b pb-2'>
                      Basic Information
                    </h3>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Name *
                    </label>
                    <input
                      type='text'
                      name='name'
                      value={recordFormData.name}
                      onChange={handleRecordInputChange}
                      required
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter full name'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Father's Name
                    </label>
                    <input
                      type='text'
                      name='father'
                      value={recordFormData.father}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder="Enter father's name"
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Parent Number
                    </label>
                    <input
                      type='tel'
                      name='parent_number'
                      value={recordFormData.parent_number}
                      onChange={handleRecordInputChange}
                      onKeyPress={(event) => {
                        if (!/[0-9]/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter parent contact number'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Mobile
                    </label>
                    <input
                      type='tel'
                      name='mobile'
                      value={recordFormData.mobile}
                      onChange={handleRecordInputChange}
                      onKeyPress={(event) => {
                        if (!/[0-9]/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter mobile number'
                    />
                  </div>

                  <div className='md:col-span-2'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Address
                    </label>
                    <input
                      type='text'
                      name='address'
                      value={recordFormData.address}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter complete address'
                    />
                  </div>

                  {/* Academic Information */}
                  <div className='lg:col-span-3'>
                    <h3 className='text-lg font-semibold text-gray-800 mb-4 mt-6 border-b pb-2'>
                      Academic Information
                    </h3>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Class
                    </label>
                    <input
                      type='text'
                      name='class'
                      value={recordFormData.class}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter class'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Stream/Subject
                    </label>
                    <input
                      type='text'
                      name='stream_subject'
                      value={recordFormData.stream_subject}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter stream or subject'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      College/School
                    </label>
                    <input
                      type='text'
                      name='collage_school'
                      value={recordFormData.collage_school}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter college or school name'
                    />
                  </div>

                  {/* Enquiry Information */}
                  <div className='lg:col-span-3'>
                    <h3 className='text-lg font-semibold text-gray-800 mb-4 mt-6 border-b pb-2'>
                      Enquiry Information
                    </h3>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Purpose of Visit
                    </label>
                    <input
                      type='text'
                      name='purpose_of_visitor'
                      value={recordFormData.purpose_of_visitor}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter purpose of visit'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Course Enquiry
                    </label>
                    <input
                      type='text'
                      name='course_enquiry'
                      value={recordFormData.course_enquiry}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter course enquiry'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Contact Method
                    </label>
                    <input
                      type='text'
                      name='contact_method'
                      value={recordFormData.contact_method}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter contact method'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Enquiry Status
                    </label>
                    <select
                      name='enquiry_status'
                      value={recordFormData.enquiry_status}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                    >
                      {enquiryStatusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option.charAt(0).toUpperCase() +
                            option.slice(1).replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      About Us Method
                    </label>
                    <input
                      type='text'
                      name='about_us_method'
                      value={recordFormData.about_us_method}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='How did you know about us?'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Initiated By
                    </label>
                    <select
                      name='init_by'
                      value={recordFormData.init_by}
                      onChange={handleRecordInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                    >
                      {initByOptions.map((option) => (
                        <option key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className='lg:col-span-3'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Notes
                    </label>
                    <textarea
                      name='note'
                      value={recordFormData.note}
                      onChange={handleRecordInputChange}
                      rows='3'
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      placeholder='Enter additional notes...'
                    ></textarea>
                  </div>
                </div>

                <div className='flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t'>
                  <button
                    onClick={handleRecordSubmit}
                    disabled={loading}
                    className='flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium disabled:cursor-not-allowed transition-colors duration-200'
                  >
                    {loading ? 'Creating...' : 'Create Record'}
                  </button>
                  <button
                    onClick={resetRecordForm}
                    className='flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors duration-200'
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Custom Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden'>
            <div className='flex items-center justify-between p-4 border-b'>
              <h2 className='text-xl font-bold text-gray-800'>
                Confirm Deletion
              </h2>
              <button
                onClick={closeDeleteConfirmation}
                className='text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100'
              >
                <X size={24} />
              </button>
            </div>
            <div className='p-4 md:p-6'>
              <p className='text-gray-700 mb-6'>
                Are you sure you want to delete this record? This action cannot
                be undone.
              </p>
              <div className='flex justify-end space-x-3'>
                <button
                  onClick={closeDeleteConfirmation}
                  className='bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors duration-200'
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={loading}
                  className='bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200'
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Update Record Form Modal */}
      {showUpdateRecordForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden'>
            <div className='flex items-center justify-between p-4 md:p-6 border-b'>
              <h2 className='text-xl md:text-2xl font-bold text-gray-800'>
                Update Record
              </h2>
              <button
                onClick={closeUpdateRecordForm}
                className='text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100'
              >
                <X size={24} />
              </button>
            </div>
            <div className='overflow-y-auto max-h-[calc(90vh-140px)]'>
              <UpdateRecordForm
                recordData={recordToUpdate}
                onUpdate={updateRecord}
                onCancel={closeUpdateRecordForm}
                loading={loading} // Pass loading state to disable form during submission
              />
            </div>
          </div>
        </div>
      )}
      {/* Followup Form Modal */}
      {showFollowupForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden'>
            <div className='flex items-center justify-between p-4 md:p-6 border-b'>
              <h2 className='text-xl md:text-2xl font-bold text-gray-800'>
                {isUpdatingFollowup ? 'Update Follow Up' : 'Create Follow Up'}
              </h2>
              <button
                onClick={closeFollowupForm}
                className='text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100'
              >
                <X size={24} />
              </button>
            </div>
            <div className='overflow-y-auto max-h-[calc(90vh-140px)]'>
              {submissionMessage && (
                <div
                  className={`p-4 mb-4 text-white rounded-lg ${
                    submissionMessage.type === 'success'
                      ? 'bg-green-500'
                      : 'bg-red-500'
                  }`}
                >
                  {submissionMessage.message}
                </div>
              )}

              <div className='p-4 md:p-6'>
                <FollowupForm
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleFormSubmit}
                  onReset={handleFormReset}
                  recordId={selectedRecordId}
                  followupId={selectedFollowupId}
                  isUpdating={isUpdatingFollowup}
                  isSubmitting={isSubmitting}
                  markAsCompleted={isUpdatingFollowup} // Ye prop pass karo
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default Record
